import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import axios from 'axios';

const ShoppingCart = () => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [addOnServices, setAddOnServices] = useState({});
  const [customFeatures, setCustomFeatures] = useState({});
  const [subscriptionTiers, setSubscriptionTiers] = useState({});
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [paymentGateway, setPaymentGateway] = useState('chapa');
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [currencyRate, setCurrencyRate] = useState(120);
  const [processing, setProcessing] = useState(false);
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const { displayCurrency, formatCurrency, convertCurrency } = useCurrency();
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchCart();
    fetchAddOnServices();
    fetchCustomFeatures();
    fetchSubscriptionTiers();
    fetchCurrencyRate();
  }, []);

  const fetchCurrencyRate = async () => {
    try {
      const response = await axios.get(`${API}/api/currency/usd-to-etb`);
      if (response.data?.rate) {
        setCurrencyRate(response.data.rate);
      }
    } catch (error) {
      console.error('[CART] Error fetching currency rate:', error);
    }
  };

  const fetchCart = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/cart`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('[CART] Response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('[CART] Cart data:', data);
        console.log('[CART] Items count:', data?.items?.length || 0);
        setCart(data);
      } else {
        console.error('[CART] Failed to fetch:', await response.text());
      }
    } catch (error) {
      console.error('[CART] Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAddOnServices = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/add-on-services`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const servicesMap = {};
        data.forEach(service => {
          servicesMap[service.id] = service;
        });
        setAddOnServices(servicesMap);
      }
    } catch (error) {
      console.error('Error fetching add-on services:', error);
    }
  };

  const fetchCustomFeatures = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/custom-add-ons`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const featuresMap = {};
        data.forEach(feature => {
          featuresMap[feature.id] = feature;
        });
        setCustomFeatures(featuresMap);
      }
    } catch (error) {
      console.error('Error fetching custom features:', error);
    }
  };

  const fetchSubscriptionTiers = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/subscription-tiers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const tiersMap = {};
        data.forEach(tier => {
          tiersMap[tier.id] = tier;
        });
        setSubscriptionTiers(tiersMap);
      }
    } catch (error) {
      console.error('Error fetching subscription tiers:', error);
    }
  };

  const removeFromCart = async (itemId) => {
    setUpdating(prev => ({ ...prev, [itemId]: true }));
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/cart/items/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        await fetchCart(); // Refresh cart
      } else {
        const error = await response.json();
        alert(`Failed to remove item: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
      alert('Error removing item. Please try again.');
    } finally {
      setUpdating(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const saveForLater = async (save = true) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/cart/save-for-later?save_for_later=${save}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        await fetchCart(); // Refresh cart
        alert(save ? 'Cart saved for later!' : 'Cart restored!');
      } else {
        const error = await response.json();
        alert(`Failed to ${save ? 'save' : 'restore'} cart: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error updating cart:', error);
      alert('Error updating cart. Please try again.');
    }
  };

  const clearCart = async () => {
    if (!window.confirm('Are you sure you want to clear your cart?')) {
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/cart`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        await fetchCart(); // Refresh cart
      } else {
        const error = await response.json();
        alert(`Failed to clear cart: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      alert('Error clearing cart. Please try again.');
    }
  };

  const getItemDetails = (item) => {
    if (item.item_type === 'subscription') {
      const tier = subscriptionTiers[item.item_id];
      return {
        name: tier?.display_name || 'Subscription Plan',
        description: tier?.description || '',
        icon: '📋'
      };
    } else {
      // Check both system add-ons and custom features
      const service = addOnServices[item.item_id] || customFeatures[item.item_id];
      return {
        name: service?.display_name || service?.name || 'Add-on Service',
        description: service?.description || '',
        icon: service?.category ? getCategoryIcon(service?.category) : '⚙️'
      };
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'capacity': '👥',
      'features': '⚡',
      'support': '🎯',
      'storage': '💾',
      'analytics': '📊'
    };
    return icons[category] || '🔧';
  };

  const formatPrice = (amount) => {
    // Use the currency context's formatCurrency which respects display currency
    return formatCurrency(amount, 'USD');
  };

  const proceedToCheckout = () => {
    setShowCheckoutDialog(true);
  };

  const handleInputChange = (field, value) => {
    setCustomerInfo(prev => ({ ...prev, [field]: value }));
  };

  const calculateTotal = () => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((sum, item) => sum + (item.total_price || 0), 0);
  };

  const handleCheckout = async () => {
    try {
      setProcessing(true);

      // Validate customer info
      if (!customerInfo.firstName || !customerInfo.lastName || !customerInfo.email) {
        alert('Please fill in all required fields');
        setProcessing(false);
        return;
      }

      if (paymentGateway === 'chapa' && !customerInfo.phone) {
        alert('Phone number is required for Chapa payments');
        setProcessing(false);
        return;
      }

      const totalUSD = calculateTotal();
      const totalETB = totalUSD * currencyRate;

      // Prepare cart items metadata
      const itemsMetadata = cart.items.map(item => ({
        item_id: item.item_id,
        item_type: item.item_type,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.total_price
      }));

      const chapaData = {
        amount: totalETB,
        currency: 'ETB',
        email: customerInfo.email,
        first_name: customerInfo.firstName,
        last_name: customerInfo.lastName,
        phone_number: customerInfo.phone,
        metadata: {
          type: 'add_ons',
          cart_id: cart.id,
          items: itemsMetadata,
          original_amount_usd: totalUSD.toString(),
          user_id: user?.id
        }
      };

      console.log('[CART] Initializing Chapa payment:', chapaData);

      const response = await axios.post(`${API}/api/payments/chapa/initialize`, chapaData);
      
      // Chapa returns checkout_url nested in data.data.checkout_url
      const checkoutUrl = response.data.data?.checkout_url || response.data.checkout_url;
      
      if (response.data.status === 'success' && checkoutUrl) {
        console.log('[CART] Redirecting to Chapa:', checkoutUrl);
        // Redirect to Chapa Checkout
        window.location.href = checkoutUrl;
      } else {
        throw new Error(response.data.message || 'Failed to initialize Chapa payment');
      }
      
    } catch (error) {
      console.error('[CART] Checkout error:', error);
      alert('Checkout failed: ' + (error.response?.data?.detail || error.message));
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🛒</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Your cart is empty</h3>
          <p className="text-gray-600 mb-6">
            Browse our add-on services to enhance your plan
          </p>
          <Button onClick={() => window.history.back()}>
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Shopping Cart</h1>
        <p className="text-gray-600">Review your selected items before checkout</p>
        
        {cart.saved_for_later && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-amber-800">🕐 This cart is saved for later</span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => saveForLater(false)}
              >
                Restore Cart
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-4">
          {cart.items.map((item) => {
            const details = getItemDetails(item);
            const isUpdating = updating[item.id];

            return (
              <Card key={item.id}>
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <span className="text-3xl">{details.icon}</span>
                    
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">{details.name}</h3>
                          <p className="text-sm text-gray-600 mt-1">{details.description}</p>
                          
                          <div className="flex items-center space-x-4 mt-3">
                            <Badge variant="outline">
                              {item.item_type === 'subscription' ? 'Subscription' : 'Add-on'}
                            </Badge>
                            
                            {item.billing_cycle && (
                              <Badge variant="secondary">
                                {item.billing_cycle === 'monthly' ? 'Monthly' : 'Annual'}
                              </Badge>
                            )}
                            
                            {item.quantity > 1 && (
                              <span className="text-sm text-gray-600">
                                Qty: {item.quantity}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <div className="font-semibold text-gray-900">
                            {formatPrice(item.total_price)}
                          </div>
                          {item.quantity > 1 && (
                            <div className="text-sm text-gray-600">
                              {formatPrice(item.unit_price)} each
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex justify-end mt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeFromCart(item.id)}
                          disabled={isUpdating}
                          className="text-red-600 hover:text-red-700"
                        >
                          {isUpdating ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                          ) : (
                            'Remove'
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>{formatPrice(cart.subtotal)}</span>
                </div>
                
                {cart.discount_amount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-{formatPrice(cart.discount_amount)}</span>
                  </div>
                )}
                
                {cart.tax_amount > 0 && (
                  <div className="flex justify-between">
                    <span>Tax</span>
                    <span>{formatPrice(cart.tax_amount)}</span>
                  </div>
                )}
                
                <div className="border-t pt-2">
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Total</span>
                    <span>{formatPrice(cart.total_amount)}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={proceedToCheckout}
                  disabled={cart.saved_for_later}
                >
                  Proceed to Checkout
                </Button>
                
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => saveForLater(!cart.saved_for_later)}
                  >
                    {cart.saved_for_later ? 'Restore' : 'Save for Later'}
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={clearCart}
                  >
                    Clear Cart
                  </Button>
                </div>
              </div>

              <div className="text-xs text-gray-500 space-y-1">
                <p>• Secure checkout with Stripe & Chapa</p>
                <p>• 30-day money-back guarantee</p>
                <p>• Cancel anytime for recurring items</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Checkout Dialog - Direct Chapa Style */}
      <Dialog open={showCheckoutDialog} onOpenChange={setShowCheckoutDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Complete Your Purchase</DialogTitle>
            <DialogDescription>
              Enter your information to proceed with checkout
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Customer Information */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={customerInfo.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder="John"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={customerInfo.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={customerInfo.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="john@example.com"
                />
              </div>

              {paymentGateway === 'chapa' && (
                <div>
                  <Label htmlFor="phone">Phone Number * (with country code)</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+251912345678"
                  />
                </div>
              )}
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <RadioGroup value={paymentGateway} onValueChange={setPaymentGateway}>
                <div className="flex items-center space-x-2 border rounded-lg p-3">
                  <RadioGroupItem value="chapa" id="chapa" />
                  <Label htmlFor="chapa" className="flex-1 cursor-pointer">
                    <div className="font-medium">Chapa Payment (ETB)</div>
                    <div className="text-xs text-gray-500">Cards, Mobile Money, Bank Transfer</div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Order Summary */}
            <div className="border-t pt-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal ({cart?.items?.length || 0} items)</span>
                  <span className="font-medium">{formatPrice(calculateTotal())}</span>
                </div>
                {paymentGateway === 'chapa' && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Payment in ETB (Rate: {currencyRate})</span>
                    <span className="font-medium">{(calculateTotal() * currencyRate).toFixed(2)} ETB</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>Total</span>
                  <span>
                    {paymentGateway === 'chapa' 
                      ? `${(calculateTotal() * currencyRate).toFixed(2)} ETB`
                      : formatPrice(calculateTotal())
                    }
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowCheckoutDialog(false)}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={handleCheckout}
                disabled={processing}
              >
                {processing ? 'Processing...' : 'Pay with Chapa'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShoppingCart;